import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { shuffle } from 'lodash';
import MarkdownPreview from '@uiw/react-markdown-preview';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUp } from "@fortawesome/free-solid-svg-icons";
import { LoadingSkeleton } from '../common/LoadingSkeleton.js';
import LoadingSpinner from '../common/LoadingSpinner.js'
import { askGitbookStream, getSuggestedQuestions } from '../../utilities/gitbookApi';

const ORG_ID = process.env.REACT_APP_GITBOOK_ORG_ID;
const GITBOOK_AI_DOCS_URL = "https://docs.gitbook.com/creating-content/searching-your-content/gitbook-ai";

const GitbookSearchModal = ({ show, onClose, initialQuery = '' }) => {
  const [query, setQuery] = useState(initialQuery);
  const [submittedQuery, setSubmittedQuery] = useState(initialQuery?.trim() || '');
  const [error, setError] = useState(null);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [followupQuestions, setFollowupQuestions] = useState([]);
  const [sources, setSources] = useState([]);
  const [streamAnswer, setStreamAnswer] = useState('');
  const [loadingAnswer, setLoadingAnswer] = useState(false);
  const [loadingAdditional, setLoadingAdditional] = useState(false);

  const cancelRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (show && !query) {
      getSuggestedQuestions()
        .then(questions => setSuggestedQuestions(shuffle(questions).slice(0, 3)))
        .catch(err => console.error("Failed to load suggested questions", err));
    }
  }, [show, query]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const submitQuestion = useCallback((questionText) => {
    const nextQuery = questionText?.trim();

    if (!nextQuery) {
      setError("Please enter a question.");
      return;
    }

    setError(null);
    setQuery(nextQuery);
    setSubmittedQuery(nextQuery);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    submitQuestion(query);
  }

  const handleChunk = useCallback((chunk) => {
    const newMarkdown = chunk?.answer?.answer?.markdown;
    if (newMarkdown) {
      setLoadingAnswer(false);
      setStreamAnswer(newMarkdown);
    }
  }, []);

  const fetchGitbookAnswer = useCallback(async () => {
    if (!submittedQuery) return;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    cancelRef.current?.abort();

    const controller = new AbortController();
    cancelRef.current = controller;

    setLoadingAnswer(true);
    setLoadingAdditional(true);
    setFollowupQuestions([]);
    setSources([]);
    setStreamAnswer('');
    setError(null);

    try {
      const data = await askGitbookStream(
        submittedQuery,
        (chunk) => {
          if (requestId !== requestIdRef.current) return;
          handleChunk(chunk);
        },
        {
          signal: controller.signal
        }
      );

      if (requestId !== requestIdRef.current) return;

      setFollowupQuestions(data?.followupQuestions || []);
      setSources(data?.sources || []);
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (requestId !== requestIdRef.current) return;

      console.error("GitBook API error:", err);
      setError("Sorry, something went wrong while searching the documentation.");
    } finally {
      if (requestId === requestIdRef.current) {
        setLoadingAnswer(false);
        setLoadingAdditional(false);
      }
    }
  }, [submittedQuery, handleChunk]);

  useEffect(() => {
    fetchGitbookAnswer();
  }, [fetchGitbookAnswer]);

  const renderSources = () => {
    if (!sources.length) return null;
    return (
      <div className="modal-sources">
        <h6 className="modal-sources-title">Sources</h6>
        <ul className="modal-sources-list">
          {sources.slice(0, 5).map((source, idx) => {
            const link = source.url || `https://app.gitbook.com/o/${ORG_ID}/s/${source.space}`;
            return (
              <li key={idx}>
                <a href={link} target="_blank" rel="noopener noreferrer" className="modal-source-link">
                  {source.name}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const isSafeHref = (href = '') => {
    try {
      const url = new URL(href, window.location.origin);
      return ['http:', 'https:', 'mailto:'].includes(url.protocol);
    } catch {
      return false;
    }
  };

  const SafeMarkdownLink = ({ href, children, ...props }) => {
    if (!isSafeHref(href)) {
      return <span className="modal-disabled-link">{children}</span>;
    }

    return (
      <a
        {...props}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  };

  if (!show) return null;

  return ReactDOM.createPortal(
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h2 className="modal-title">Documentation search</h2>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>

        <div className="gitbook-modal-body">
          <form className="gitbook-modal-form" onSubmit={handleSubmit}>
            <input
              className="gitbook-modal-input"
              type="text"
              placeholder="Ask a question..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />

            <button
              className="modal-search-button"
              type="submit"
              disabled={!query?.trim() || loadingAnswer}
              aria-label="Submit question"
              title="Submit question"
            >
              <FontAwesomeIcon icon={faArrowUp}/>
            </button>
          </form>

          {error && (
            <p className="modal-error" role="alert">
              {error}
            </p>
          )}

          {loadingAnswer && (<><LoadingSpinner /><p className="modal-loading">Analyzing the content to answer your question...</p></>)}

          {suggestedQuestions.length > 0 && !query && (
            <div className="modal-suggested">
              <h6 className="modal-suggested-title">Suggested Questions</h6>
              <ul className="modal-suggested-list">
                {suggestedQuestions.map((q, idx) => (
                  <li
                    key={idx}
                    className="modal-suggested-item"
                    onClick={() => submitQuestion(q)}
                  >
                    {q}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {streamAnswer && (
            <div className="modal-answer">
              <h6 className="modal-answer-title">Answer</h6>
              <MarkdownPreview
                source={streamAnswer}
                style={{ background: 'transparent', color: 'black' }}
                components={{ a: SafeMarkdownLink }}
              />
              {loadingAdditional ? (
                <div className="modal-sources">
                  <h6 className="modal-sources-title">Sources</h6>
                  <LoadingSkeleton count={2} minWidth={50} maxWidth={90} />
                </div>
              ) : (
                renderSources()
              )}
            </div>
          )}

          <br />

          {loadingAdditional && streamAnswer && (
            <div className="modal-related">
              <h6 className="modal-related-title">Related Questions</h6>
              <LoadingSkeleton count={3} minWidth={60} maxWidth={95} />
            </div>
          )}

          {!loadingAdditional && followupQuestions.length > 0 && (
            <div className="modal-related">
              <h6 className="modal-related-title">Related Questions</h6>
              <ul className="modal-related-list">
                {followupQuestions.map((q, idx) => (
                  <li
                    key={idx}
                    className="modal-related-item"
                    onClick={() => submitQuestion(q)}
                  >
                    {q}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="gitbook-modal-footer">
            <span>
              Powered by{' '}
              <a
                href={GITBOOK_AI_DOCS_URL}
                className='gitbook-footer-link'
                target="_blank"
                rel="noopener noreferrer"
              >
                GitBook AI
              </a>
            </span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GitbookSearchModal;
